import { memo, useEffect, useRef } from 'react'

import { Refresh } from '@mui/icons-material'
import { Box, Tooltip } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { Navigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { Editor, EditorInstance } from '@shared/components/Editor'
import { AnimatedCounterButton } from '@shared/components/MaterialUIEnhancements/IconButton'
import { LoadingIndicator } from '@shared/layouts'
import { eventsRoute } from 'pages/NWPlatform/Workspaces/Workspace/Events'
import { eventExpManifestRoute } from 'pages/NWPlatform/Workspaces/Workspace/Events/Event'
import { eventQueryKeys } from 'services/queryKeys'

const ExpManifestRaw = () => {
  const { t } = useTranslation()
  const editorInstanceRef = useRef<EditorInstance>(undefined)

  const { eventId } = eventExpManifestRoute.useParams()

  const expManifestQuery = useQuery(eventQueryKeys.expManifest({ eventId }))
  const { data: expManifest, refetch } = expManifestQuery

  useEffect(() => {
    if (editorInstanceRef.current && expManifestQuery.isSuccess) {
      editorInstanceRef.current.getModel()?.setValue(window.JSON.stringify(expManifest, null, 4))
    }
  }, [expManifest, expManifestQuery.isSuccess])

  if (expManifestQuery.isLoading) {
    return <LoadingIndicator />
  }

  if (!expManifest) {
    return <Navigate replace from={eventExpManifestRoute.fullPath} to={eventsRoute.to} />
  }

  return (
    <Box sx={{ flex: 1, display: 'flex' }}>
      <Editor
        CustomActionsSlot={
          <Tooltip title={t('refresh')} placement="left">
            <AnimatedCounterButton action={refetch}>
              {({ key, ...props }) => <Refresh key={key} {...props} />}
            </AnimatedCounterButton>
          </Tooltip>
        }
        value={expManifest}
        readOnly
        readOnlyLabel={t('readOnlyAutoRefresh')}
        getEditorInstance={(editorInstance: EditorInstance) => void (editorInstanceRef.current = editorInstance)}
      />
    </Box>
  )
}

export const ExpManifest = memo(ExpManifestRaw)
